<template>
    <form @submit="submitForm($event)">
        <OtInputField :label="$t('login.login.email')">
            <input
                :aria-label="$t('login.login.email').toString()"
                type="email"
                name="email"
                v-model="email"
                :placeholder="$t('login.login.email').toString()"
                :invalid="error"
                @animationstart="onAutofill"
                @input="onInput"
                autofocus
            />
        </OtInputField>
        <OtInputField :label="$t('login.login.password')" for="login-password">
            <input
                :aria-label="$t('login.login.password').toString()"
                type="password"
                name="password"
                @change="onChange"
                v-model="password"
                :placeholder="$t('login.login.password').toString()"
                :invalid="error"
                @input="onInput"
            />
        </OtInputField>
        <div class="lost-password">
            <OtButton
                class="ot-button is-text is-small"
                type="button"
                @click="$router.push({name: 'password.recovery', query: $route.query})"
                :title="$t('login.login.lost_password.title').toString()"
            >
                {{ $t('login.login.lost_password.text') }}
            </OtButton>
        </div>
        <div class="signup" v-if="showSignup">
            <span class="ot-text-small">{{ $t('login.login.sign_up.suffix') }}</span>
            <OtButton
                class="ot-button is-text is-small"
                type="button"
                @click="$router.push({name: 'signup', query: $route.query})"
                :title="$t('login.login.sign_up.title').toString()"
            >
                {{ $t('login.login.sign_up.text') }}
            </OtButton>
        </div>
        <div class="footer">
            <OtButton v-if="submitting"
                class="footer__right ot-button is-loading"
            />
            <OtButton
                v-else
                class="footer__right ot-button is-fullwidth"
                type="submit"
                :disabled="!formCompleted"
                :title="$t('login.login.submit.title').toString()"
            >
                {{ $t('login.login.submit.text') }}
                <i class="oti oti-arrow-right ot-button-icon--right"/>
            </OtButton>
        </div>
    </form>
</template>

<script lang="ts">

import { Component, Prop, Vue } from 'vue-property-decorator';

import { OtInputField } from '@openticket/vue-input';
import HeaderCentered from './HeaderCentered.vue';
import OtButton from './OtButton.vue';
import {
    combineCredentialsActions,
    CREDENTIALS_ACTION,
    getCredentialsActionOverride,
} from '../services/util/signInAction';

@Component({
    components: {
        OtInputField, HeaderCentered, OtButton,
    },
})
export default class EmailForm extends Vue {

    @Prop({ default: null }) error!: string | null;

    @Prop({ default: false }) submitting!: boolean;

    email: string | null = null;

    password: string | null = null;

    isAutofilled = false;

    onInput(): void {
        this.$emit('input');
    }

    onChange(e: Event): void {
        if (e.target instanceof HTMLInputElement && e.target.value) {
            this.password = e.target.value;
        }
    }

    onAutofill(e: AnimationEvent): void {
        if (e.animationName !== 'onAutoFillStart') {
            return;
        }

        this.isAutofilled = true;
    }

    get showSignup(): boolean {
        // DD-4E8F - The signup link is shown by default, unless specifically hidden by (login/sso) settings.
        return combineCredentialsActions(
            this.$whitelabel.login.credentials?.action ?? '',
            getCredentialsActionOverride(), // query: login_credentials={action}
        ) !== CREDENTIALS_ACTION.HIDE_SIGNUP;
    }

    get formCompleted(): boolean {
        if (this.isAutofilled) {
            return true;
        }

        return !!this.email && !!this.password;
    }

    submitForm(e: Event): void {
        e.preventDefault();

        if (this.formCompleted) {
            this.login();
        }
    }

    login(): void {
        this.$emit('submit', this.email, this.password);
    }

}
</script>

<style lang="scss" scoped>
.warning.remove-margin-bottom {
    margin-bottom: 0;
}

.warning {
    color: var(--ot-color-accent-orange-dark);
    text-align: center;
    margin-bottom: var(--ot-spacing-3xl);
}

.lost-password {
    color: var(--ot-color-core-brand);

    .ot-button {
        padding-bottom: 0;
    }
}

.signup {
    .ot-button {
        padding-top: 0;
        padding-bottom: 0;
        height: auto;
        color: var(--ot-color-core-brand);
        margin-left: .25rem;
    }
}

.ot-content input[type="email"]:invalid:not(:focus) {
    border: var(--ot-input-border);
    box-shadow: 0 0 0 1px var(--ot-input-border-color);
}

.ot-content input[type="email"]:invalid {
    background: var(--ot-input-background);
    color: var(--ot-input-color);
}

.footer {
    display: flex;
    margin-top: var(--ot-spacing-2xl);
    flex-wrap: wrap-reverse;
    gap: var(--ot-spacing-default);

    @media (min-width: 40rem) {
        flex-wrap: nowrap;
    }

    &__left, &__right {
        flex: auto;
    }
}
</style>
